import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import logo from '../assets/logo.svg';
import bgImage from '../assets/bg.webp';

/**
 * Landing page for the link sent to the NEW address of an admin-initiated
 * email change (platform-internal accounts only).
 *
 * Opening the page never changes anything: it only validates the link (GET).
 * The change happens on the explicit button press (POST), so a mail scanner
 * that prefetches the link cannot complete it.
 */
type Phase = 'validating' | 'invalid' | 'ready' | 'confirming' | 'done';

const INVALID = 'This email-change link is invalid or has expired. Ask your LevelUp administrator for a new one.';

export function ConfirmEmailChangePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [phase, setPhase] = useState<Phase>('validating');
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError(INVALID);
      setPhase('invalid');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/auth/email-change/validate?token=${encodeURIComponent(token)}`);
        const data = await res.json();
        if (cancelled) return;
        if (res.ok && data.valid) {
          setNewEmail(data.newEmail || '');
          setPhase('ready');
        } else {
          setError(INVALID);
          setPhase('invalid');
        }
      } catch {
        if (!cancelled) {
          setError('Could not check this link. Please try again.');
          setPhase('invalid');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const confirm = async () => {
    setPhase('confirming');
    setError('');
    try {
      const res = await fetch('/api/auth/email-change/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setNewEmail(data.newEmail || newEmail);
        setPhase('done');
      } else {
        setError(INVALID);
        setPhase('invalid');
      }
    } catch {
      setError('Network error. Please try again.');
      setPhase('ready');
    }
  };

  return (
    <div
      className="min-h-screen bg-[rgb(24,24,24)] bg-cover bg-center flex flex-col items-center p-4"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      <img src={logo} alt="LevelUp" className="h-10 mt-8" />
      <div className="flex-1 flex items-center justify-center w-full">
        <div className="bg-white shadow-lg rounded-lg p-8 w-full max-w-md text-center">
          {phase === 'validating' && (
            <p className="text-gray-500" data-testid="email-change-validating">Checking your link...</p>
          )}

          {phase === 'invalid' && (
            <div data-testid="email-change-invalid">
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">Link not valid</h1>
              <p className="text-sm text-gray-500 mb-6">{error}</p>
              <Link to="/auth/login" className="text-sm text-green-700 hover:underline">
                Go to sign in
              </Link>
            </div>
          )}

          {(phase === 'ready' || phase === 'confirming') && (
            <div data-testid="email-change-ready">
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">Confirm your new sign-in email</h1>
              <p className="text-sm text-gray-600 mb-2">
                A LevelUp administrator asked to make <strong>{newEmail}</strong> the sign-in email for an internal LevelUp account.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Confirm only if you expected this. The account will be signed out everywhere; sign in again with this address.
              </p>
              {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
              <button
                type="button"
                onClick={confirm}
                disabled={phase === 'confirming'}
                className="w-full bg-green-700 text-white py-2 px-4 rounded-md font-medium hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {phase === 'confirming' ? 'Confirming...' : 'Confirm new email'}
              </button>
            </div>
          )}

          {phase === 'done' && (
            <div data-testid="email-change-done">
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">Sign-in email changed</h1>
              <p className="text-sm text-gray-500 mb-6">
                Sign in with <strong>{newEmail}</strong>. All previous sessions were signed out.
              </p>
              <Link
                to="/auth/login"
                className="inline-block bg-green-700 text-white py-2 px-4 rounded-md font-medium hover:bg-green-800 transition-colors"
              >
                Continue to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
